/**
 * ChecklistRecordsContext.js
 *
 * FT-324 cycle 3 (2026-07-07): React context wrapper for `useChecklistRecords`.
 *
 * Purpose
 * -------
 * `useChecklistRecords` MUST mount eagerly, high in the tree, ONCE per
 * Operations session — the same pattern that `useMainLogbook` already uses
 * successfully to survive cross-device UI refresh. Cycle-2 verified
 * empirically:
 *
 *   - `useMainLogbook` (eager mount in OperationsModule): 3/3 cross-device PASS
 *   - Dashboard badge / `useDailyChecks` (eager mount at shell level): 1/1 PASS
 *   - `useChecklistRecords` (lazy mount inside ChecklistsTab): 0/5 FAIL
 *
 * The IDB-only hydration path works fine — the failure was pure mount timing.
 * By the time Device B's user clicks the Checklists tab, the `useStorageState`
 * value inside the tab's just-mounted hook instance shows what THAT instance
 * hydrated, which is empty until IDB read resolves; meanwhile the sync
 * bridge's `sr:sync:hydrate` event has already fired against an unmounted
 * listener. Even after IDB read resolves, subsequent Device A writes reach
 * IDB but the tab hook's `sr:sync:hydrate` listener is only re-attached on
 * each tab-mount cycle.
 *
 * Fix
 * ---
 * Mount `useChecklistRecords` ONCE at OperationsModule level (next to
 * `useMainLogbook`). All 6 consumers read the same hook instance through
 * this context. The hook lives for the entire Operations session, its
 * `sr:sync:hydrate` listener is always attached, and its `useStorageState`
 * has already resolved its async IDB read long before any tab click.
 *
 * Preservation invariants
 * -----------------------
 * - FT-311 storage-first: `useChecklistRecords.js` internals UNCHANGED.
 * - FT-324 cycle-2 migration + useStorageState migration: UNCHANGED.
 * - FT-309 auto-log to logbook (ChecklistsTab.onComplete callback level):
 *   UNCHANGED — callback-level, orthogonal to hook-instance identity.
 *
 * Consumers switch from `useChecklistRecords()` → `useChecklistRecordsContext()`.
 * Their destructures stay identical (each consumer uses a subset of the API).
 */

import React, { createContext, useContext } from 'react';

const ChecklistRecordsContext = createContext(null);

/**
 * Provider — wraps children with a single `useChecklistRecords` instance.
 *
 * `value` MUST be the result of calling `useChecklistRecords()` once at the
 * OperationsModule level. All descendants that call
 * `useChecklistRecordsContext()` will share this single instance.
 */
export const ChecklistRecordsProvider = ({ value, children }) => {
  return (
    <ChecklistRecordsContext.Provider value={value}>
      {children}
    </ChecklistRecordsContext.Provider>
  );
};

/**
 * Consumer hook — reads the single `useChecklistRecords` instance from
 * context. Throws a clear error if used outside a `ChecklistRecordsProvider`
 * so misuse fails loud at development time (rather than silently rendering
 * an empty Recent list, which was exactly the class of bug this cycle
 * exists to eliminate).
 */
export const useChecklistRecordsContext = () => {
  const ctx = useContext(ChecklistRecordsContext);
  if (ctx === null) {
    throw new Error(
      'useChecklistRecordsContext must be used within a ChecklistRecordsProvider. ' +
      'Mount <ChecklistRecordsProvider value={useChecklistRecords()}> at the ' +
      'OperationsModule level (see FT-324 cycle 3).'
    );
  }
  return ctx;
};

export default ChecklistRecordsContext;
